import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, CheckCircle, XCircle, Loader2, 
  Calendar, FileText, AlertTriangle, ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ReportVerificationPage = () => {
  const { caseReference } = useParams();
  const [loading, setLoading] = useState(true);
  const [verification, setVerification] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyReport = async () => {
      if (!caseReference) {
        setError('No case reference provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/verify/${caseReference}`);
        
        if (!response.ok) {
          throw new Error('Verification failed');
        }

        const data = await response.json();
        setVerification(data);
      } catch (err) {
        console.error('Verification error:', err);
        setError('Unable to verify report. Please check the case reference.');
      } finally {
        setLoading(false);
      }
    };

    verifyReport();
  }, [caseReference]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Verifying report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16" data-testid="verification-page">
      <Helmet>
        <title>Report Verification | ActionFraudOne</title>
        <meta name="description" content="Verify the authenticity of a ActionFraudOne fraud intelligence report." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-[#C3A86B] mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Report Verification</h1>
            <p className="text-gray-400">ActionFraudOne</p>
          </div>

          {error && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-8 text-center">
                <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Verification Failed</h2>
                <p className="text-gray-400 mb-6">{error}</p>
                <p className="text-gray-500 text-sm">
                  Case Reference: <span className="text-white font-mono">{caseReference}</span>
                </p>
              </CardContent>
            </Card>
          )}

          {verification && !verification.verified && (
            <Card className="bg-yellow-500/10 border-yellow-500/30">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Report Not Found</h2>
                <p className="text-gray-400 mb-4">{verification.message}</p>
                <p className="text-gray-500 text-sm">
                  The case reference <span className="text-white font-mono">{caseReference}</span> could not be verified in our system.
                </p>
              </CardContent>
            </Card>
          )}

          {verification && verification.verified && (
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2">Verified Report</h2>
                  <p className="text-green-400 font-medium">{verification.message}</p>
                </div>

                <div className="bg-[#0a1628]/50 rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                    <span className="text-gray-400">Case Reference</span>
                    <span className="text-[#C3A86B] font-mono font-semibold">{verification.case_reference}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                    <span className="text-gray-400">Entity Checked</span>
                    <span className="text-white">{verification.entity_checked}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                    <span className="text-gray-400">Risk Level</span>
                    <span className={`font-semibold ${
                      verification.risk_level?.toLowerCase().includes('high') ? 'text-red-400' :
                      verification.risk_level?.toLowerCase().includes('moderate') ? 'text-orange-400' :
                      verification.risk_level?.toLowerCase().includes('low') ? 'text-green-400' :
                      'text-gray-300'
                    }`}>
                      {verification.risk_level}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                    <span className="text-gray-400">Report Type</span>
                    <span className="text-white capitalize">{verification.report_type?.replace('_', ' ')}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                    <span className="text-gray-400">Issued Date</span>
                    <span className="text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      {verification.issued_date ? new Date(verification.issued_date).toLocaleDateString('en-GB', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-400">Status</span>
                    <span className="text-green-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {verification.status?.charAt(0).toUpperCase() + verification.status?.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-[#0a1628]/50 rounded-lg border border-[#1e40af]/20">
                  <p className="text-gray-400 text-sm text-center">
                    This report was issued by ActionFraudOne Ltd. 
                    If you have any questions about this report, please contact us at{' '}
                    <a href="mailto:info@actionfraudone.com" className="text-cyan-400 hover:underline">
                      info@actionfraudone.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Links */}
          <div className="text-center mt-8">
            <Link to="/check" className="text-cyan-400 hover:text-cyan-300 text-sm">
              Run a Scam Check
            </Link>
            <span className="text-gray-600 mx-3">|</span>
            <Link to="/" className="text-gray-400 hover:text-gray-300 text-sm">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportVerificationPage;
